/**
 * selectionMath.ts
 * Pure helpers for Excel-style rectangular range selection in the Skill Matrix.
 *
 * All functions are stateless and take plain arrays/objects as input so they
 * are trivially testable without any store/component setup.
 */

import type { SkillUser, MatrixSkill } from '../../../../services/scheduleSkillMatrix';

/** Identifies a single cell by its row key and column key. */
export interface CellCoord {
  userKey: string;
  skillKey: string;
}

/**
 * Compute the rectangular set of coordinates spanned by `anchor` and `focus`.
 *
 * The rectangle is determined by the positions of anchor and focus within
 * `users` (rows) and `skills` (columns). The returned array is in row-major
 * order (row 0 col 0, row 0 col 1 … row N col M).
 *
 * Edge cases:
 * - Either coord is null  → returns [] (no selection).
 * - Same cell for anchor and focus → returns a single-element array.
 * - Focus/anchor reversed (focus is above/left of anchor) → still works;
 *   we normalise to min/max indices before iterating.
 * - A key is not found in the arrays (stale after filter change) → skip it
 *   silently; the caller should clear selection when filters change.
 */
export function resolveSelectedCoords(
  anchor: CellCoord | null,
  focus: CellCoord | null,
  users: SkillUser[],
  skills: MatrixSkill[],
): CellCoord[] {
  if (anchor === null || focus === null) return [];

  const anchorRowIdx = users.findIndex((u) => u.AppUserKey === anchor.userKey);
  const focusRowIdx  = users.findIndex((u) => u.AppUserKey === focus.userKey);
  const anchorColIdx = skills.findIndex((tt) => tt.SkillKey === anchor.skillKey);
  const focusColIdx  = skills.findIndex((tt) => tt.SkillKey === focus.skillKey);

  // If either end is not visible (stale key after filter), return empty.
  if (anchorRowIdx === -1 || focusRowIdx === -1 || anchorColIdx === -1 || focusColIdx === -1) {
    return [];
  }

  const minRow = Math.min(anchorRowIdx, focusRowIdx);
  const maxRow = Math.max(anchorRowIdx, focusRowIdx);
  const minCol = Math.min(anchorColIdx, focusColIdx);
  const maxCol = Math.max(anchorColIdx, focusColIdx);

  const result: CellCoord[] = [];
  for (let r = minRow; r <= maxRow; r++) {
    for (let c = minCol; c <= maxCol; c++) {
      result.push({
        userKey: users[r].AppUserKey,
        skillKey: skills[c].SkillKey,
      });
    }
  }
  return result;
}

/** Build a Set<string> of composite keys from a CellCoord array for O(1) lookup. */
export function buildSelectionSet(coords: CellCoord[]): Set<string> {
  const s = new Set<string>();
  for (const c of coords) {
    s.add(`${c.userKey}::${c.skillKey}`);
  }
  return s;
}

/** Returns the composite key for a cell (matches cellIndex key format). */
export function cellKey(userKey: string, skillKey: string): string {
  return `${userKey}::${skillKey}`;
}

/**
 * Describes which edges of the selection rectangle a particular cell touches.
 * Used to draw the solid Excel-style perimeter box around a multi-cell selection.
 */
export interface SelectionEdges {
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
}

/**
 * For a selection spanning 2+ cells, returns a map (composite key → which edges
 * of the selection rectangle that cell sits on). Used to draw a solid perimeter
 * box around the whole selection (Excel-style).
 *
 * Returns an EMPTY map when the selection is empty or a single cell — single
 * cells get no perimeter box (focus is shown via the chip ring, not a cell ring).
 */
export function computeSelectionEdges(
  anchor: CellCoord | null,
  focus: CellCoord | null,
  users: SkillUser[],
  skills: MatrixSkill[],
): Map<string, SelectionEdges> {
  const coords = resolveSelectedCoords(anchor, focus, users, skills);
  if (coords.length < 2) return new Map();

  const anchorRowIdx = users.findIndex((u) => u.AppUserKey === (anchor as CellCoord).userKey);
  const focusRowIdx  = users.findIndex((u) => u.AppUserKey === (focus as CellCoord).userKey);
  const anchorColIdx = skills.findIndex((tt) => tt.SkillKey === (anchor as CellCoord).skillKey);
  const focusColIdx  = skills.findIndex((tt) => tt.SkillKey === (focus as CellCoord).skillKey);

  // Guard: if any index is missing, resolveSelectedCoords already returned [], so
  // coords.length < 2 above would have caught it. But defensive check here.
  if (anchorRowIdx === -1 || focusRowIdx === -1 || anchorColIdx === -1 || focusColIdx === -1) {
    return new Map();
  }

  const minRow = Math.min(anchorRowIdx, focusRowIdx);
  const maxRow = Math.max(anchorRowIdx, focusRowIdx);
  const minCol = Math.min(anchorColIdx, focusColIdx);
  const maxCol = Math.max(anchorColIdx, focusColIdx);

  const result = new Map<string, SelectionEdges>();
  for (const coord of coords) {
    const rowIdx = users.findIndex((u) => u.AppUserKey === coord.userKey);
    const colIdx = skills.findIndex((tt) => tt.SkillKey === coord.skillKey);
    result.set(cellKey(coord.userKey, coord.skillKey), {
      top:    rowIdx === minRow,
      bottom: rowIdx === maxRow,
      left:   colIdx === minCol,
      right:  colIdx === maxCol,
    });
  }
  return result;
}
